import { useMemo, useState, useEffect } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, Search, Sparkles } from "lucide-react";

import { ProfileOverviewHeader } from "@/components/panel/ProfileOverviewHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { useToast } from "@/hooks/use-toast";
import { computeKarmaNumerology, parseBirthDate } from "@/lib/karma/numerology";
import { sendProgressEvent } from "@/lib/progress";
import { createSocialPost } from "@/lib/supabase/social";

type DietPlan = {
  id: string;
  title: string;
  description: string;
  coverImageUrl?: string;
  waterTargetLiters?: number;
  mealType?: string;
  active: boolean;
  tags: string[];
};

type DietsResponse = {
  ok: true;
  items: DietPlan[];
};

type WellnessActivity = {
  id: string;
  title: string;
  description: string;
  durationMinutes?: number;
  difficulty?: "easy" | "medium" | "hard";
  iconKey?: string;
  active: boolean;
  tags: string[];
};

type ActivitiesResponse = {
  ok: true;
  items: WellnessActivity[];
};

async function fetchDiets(): Promise<DietPlan[]> {
  const res = await fetch("/api/numeroloji/diets");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Diyetler alınamadı.");
  }
  const data = json as DietsResponse;
  return Array.isArray(data?.items) ? data.items : [];
}

async function fetchActivities(): Promise<WellnessActivity[]> {
  const res = await fetch("/api/numeroloji/activities");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Aktiviteler alınamadı.");
  }
  const data = json as ActivitiesResponse;
  return Array.isArray(data?.items) ? data.items : [];
}

function normalizeTag(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z]+/g, "");
}

const PROGRAM_DAYS_TAG_PREFIX = "program_gun:";
const PROGRAM_DAY_TRACKING_KEY = "numeroloji_program_day_tracking_v1";
const DIET_TASK_PROGRESS_KEY = "numeroloji_diet_task_progress_v1";
const DIET_TASK_DAY_PREFIXES = ["diyet_program_gun:", "program_gun:"] as const;
const DIET_TASK_POINTS_PREFIXES = ["diyet_gorev_puan:", "gorev_puan:", "task_puan:"] as const;

type ProgramDayTrackingState = {
  startedAtByItemId: Record<string, string>;
};

type ProgramDayTrackingStore = Record<string, ProgramDayTrackingState>;

type DietTaskProgressState = {
  completedTaskIdsByDietIdByDate: Record<string, Record<string, string[]>>;
};

type DietTaskProgressStore = Record<string, DietTaskProgressState>;

type DietProgramTask = {
  id: string;
  title: string;
  description: string;
  dayNumber: number;
  points: number;
};

function formatLocalDateKey(d: Date) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseProgramDaysFromTags(tags: string[]) {
  const raw = (tags ?? [])
    .find((tag) => String(tag ?? "").startsWith(PROGRAM_DAYS_TAG_PREFIX))
    ?.slice(PROGRAM_DAYS_TAG_PREFIX.length) ?? "";

  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 0;
}

function stripProgramDaysTag(tags: string[]) {
  return (tags ?? []).filter((tag) => !String(tag ?? "").startsWith(PROGRAM_DAYS_TAG_PREFIX));
}

function loadProgramDayTracking(userKey: string): ProgramDayTrackingState {
  if (typeof window === "undefined") return { startedAtByItemId: {} };

  try {
    const raw = window.localStorage.getItem(PROGRAM_DAY_TRACKING_KEY);
    if (!raw) return { startedAtByItemId: {} };
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return { startedAtByItemId: {} };

    const store = parsed as ProgramDayTrackingStore;
    const state = store[userKey];
    if (!state || typeof state !== "object") return { startedAtByItemId: {} };

    return {
      startedAtByItemId:
        state.startedAtByItemId && typeof state.startedAtByItemId === "object"
          ? (state.startedAtByItemId as Record<string, string>)
          : {},
    };
  } catch {
    return { startedAtByItemId: {} };
  }
}

function saveProgramDayTracking(userKey: string, state: ProgramDayTrackingState) {
  if (typeof window === "undefined") return;

  try {
    const raw = window.localStorage.getItem(PROGRAM_DAY_TRACKING_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    const base = parsed && typeof parsed === "object" ? (parsed as ProgramDayTrackingStore) : {};

    const next: ProgramDayTrackingStore = {
      ...base,
      [userKey]: state,
    };

    window.localStorage.setItem(PROGRAM_DAY_TRACKING_KEY, JSON.stringify(next));
  } catch {
    // ignore local storage errors
  }
}

function resolveProgramDay(startedDateKey: string | undefined, todayKey: string) {
  if (!startedDateKey) return 0;
  const start = new Date(`${startedDateKey}T00:00:00`);
  const today = new Date(`${todayKey}T00:00:00`);
  const startMs = start.getTime();
  const todayMs = today.getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(todayMs) || todayMs < startMs) return 1;
  return Math.floor((todayMs - startMs) / 86_400_000) + 1;
}

function parsePositiveNumberTag(tags: string[], prefixes: readonly string[]) {
  const raw =
    (tags ?? [])
      .map((tag) => String(tag ?? "").trim())
      .find((tag) => prefixes.some((prefix) => tag.startsWith(prefix))) ?? "";

  const prefix = prefixes.find((item) => raw.startsWith(item));
  if (!prefix) return 0;

  const parsed = Number(raw.slice(prefix.length));
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 0;
}

function parseDietTaskDay(tags: string[]) {
  return parsePositiveNumberTag(tags, DIET_TASK_DAY_PREFIXES);
}

function parseDietTaskPoints(activity: WellnessActivity) {
  const taggedPoints = parsePositiveNumberTag(activity.tags ?? [], DIET_TASK_POINTS_PREFIXES);
  if (taggedPoints > 0) return taggedPoints;
  if (activity.difficulty === "hard") return 5;
  if (activity.difficulty === "medium") return 4;
  if (activity.difficulty === "easy") return 3;
  return 3;
}

function hasRituelTag(tags: string[]) {
  const values = new Set((tags ?? []).map((tag) => normalizeTag(tag)).filter(Boolean));
  return values.has("rituel") || values.has("ritueluygulamasi") || values.has("ritual");
}

function isWaterGoalTask(task: DietProgramTask) {
  const text = `${task.title} ${task.description}`.toLocaleLowerCase("tr-TR");
  return text.includes("su") || text.includes("water") || text.includes("hidrasyon") || text.includes("hidratasyon");
}

function loadDietTaskProgress(userKey: string): DietTaskProgressState {
  if (typeof window === "undefined") return { completedTaskIdsByDietIdByDate: {} };

  try {
    const raw = window.localStorage.getItem(DIET_TASK_PROGRESS_KEY);
    if (!raw) return { completedTaskIdsByDietIdByDate: {} };
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return { completedTaskIdsByDietIdByDate: {} };

    const store = parsed as DietTaskProgressStore;
    const state = store[userKey];
    if (!state || typeof state !== "object") return { completedTaskIdsByDietIdByDate: {} };

    return {
      completedTaskIdsByDietIdByDate:
        state.completedTaskIdsByDietIdByDate && typeof state.completedTaskIdsByDietIdByDate === "object"
          ? (state.completedTaskIdsByDietIdByDate as Record<string, Record<string, string[]>>)
          : {},
    };
  } catch {
    return { completedTaskIdsByDietIdByDate: {} };
  }
}

function saveDietTaskProgress(userKey: string, state: DietTaskProgressState) {
  if (typeof window === "undefined") return;

  try {
    const raw = window.localStorage.getItem(DIET_TASK_PROGRESS_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    const base = parsed && typeof parsed === "object" ? (parsed as DietTaskProgressStore) : {};

    const next: DietTaskProgressStore = {
      ...base,
      [userKey]: state,
    };

    window.localStorage.setItem(DIET_TASK_PROGRESS_KEY, JSON.stringify(next));
  } catch {
    // ignore local storage errors
  }
}

function getKarmicDebtTag(code: number) {
  return `karmikborc${code}`;
}

export default function UserPanelDiyet() {
  const { user } = useSupabaseSession();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [query, setQuery] = useState("");
  const userKey = user?.id ? `sb:${user.id}` : "anon";
  const todayKey = useMemo(() => formatLocalDateKey(new Date()), []);
  const [programDayTracking, setProgramDayTracking] = useState<ProgramDayTrackingState>(() => loadProgramDayTracking(userKey));
  const [dietTaskProgress, setDietTaskProgress] = useState<DietTaskProgressState>(() => loadDietTaskProgress(userKey));

  const profileInput = useMemo(() => {
    const firstName = typeof user?.metadata?.first_name === "string" ? (user?.metadata?.first_name as string) : "";
    const lastName = typeof user?.metadata?.last_name === "string" ? (user?.metadata?.last_name as string) : "";
    const fullName = `${firstName} ${lastName}`.trim();

    const birthDay = typeof user?.metadata?.birth_day === "number" ? user?.metadata?.birth_day : Number(user?.metadata?.birth_day);
    const birthMonth =
      typeof user?.metadata?.birth_month === "number" ? user?.metadata?.birth_month : Number(user?.metadata?.birth_month);
    const birthYear = typeof user?.metadata?.birth_year === "number" ? user?.metadata?.birth_year : Number(user?.metadata?.birth_year);

    if (!fullName || !Number.isFinite(birthDay) || !Number.isFinite(birthMonth) || !Number.isFinite(birthYear)) return null;

    const birthDate = parseBirthDate({
      day: birthDay,
      month: birthMonth,
      year: birthYear,
    });

    return {
      fullName,
      birthDate,
    };
  }, [
    user?.metadata?.birth_day,
    user?.metadata?.birth_month,
    user?.metadata?.birth_year,
    user?.metadata?.first_name,
    user?.metadata?.last_name,
  ]);

  const numerology = useMemo(() => {
    if (!profileInput) return null;
    return computeKarmaNumerology(profileInput.fullName, profileInput.birthDate);
  }, [profileInput]);

  const karmicCodes = numerology?.karmicCodes ?? [];

  const dietsQuery = useQuery({
    queryKey: ["panel-diets"],
    queryFn: fetchDiets,
    staleTime: 30_000,
  });

  const activitiesQuery = useQuery({
    queryKey: ["panel-activities"],
    queryFn: fetchActivities,
    staleTime: 30_000,
  });

  useEffect(() => {
    setProgramDayTracking(loadProgramDayTracking(userKey));
    setDietTaskProgress(loadDietTaskProgress(userKey));
  }, [userKey]);

  const shown = useMemo(() => {
    const items = (dietsQuery.data ?? []).filter((d) => d && d.active);

    const normalizedGeneralKeys = new Set(["genel", "general"].map(normalizeTag));
    const wanted = new Set(karmicCodes.map((code) => getKarmicDebtTag(code)).map(normalizeTag));

    const personalized: DietPlan[] = [];
    const general: DietPlan[] = [];

    for (const d of items) {
      const tagKeys = new Set((d.tags ?? []).filter(Boolean).map(normalizeTag));

      if (wanted.size) {
        for (const w of wanted) {
          if (tagKeys.has(w)) {
            personalized.push(d);
            break;
          }
        }
      } else {
        for (const g of normalizedGeneralKeys) {
          if (tagKeys.has(g)) {
            general.push(d);
            break;
          }
        }
      }
    }

    const base = wanted.size
      ? personalized.length
        ? personalized
        : general.length
          ? general
          : items
      : general.length
        ? general
        : items;

    const q = query.trim().toLocaleLowerCase("tr-TR");
    if (!q) return base;

    return base.filter((d) => {
      const hay = `${d.title} ${d.description} ${stripProgramDaysTag(d.tags ?? []).join(" ")}`.toLocaleLowerCase("tr-TR");
      return hay.includes(q);
    });
  }, [karmicCodes, dietsQuery.data, query]);

  const dailyTasksByDietId = useMemo(() => {
    const allActivities = (activitiesQuery.data ?? []).filter((item) => item && item.active && !hasRituelTag(item.tags ?? []));
    const byDietId: Record<string, DietProgramTask[]> = {};

    shown.forEach((diet) => {
      const programDay = resolveProgramDay(programDayTracking.startedAtByItemId[diet.id], todayKey);
      if (programDay <= 0) {
        byDietId[diet.id] = [];
        return;
      }

      const normalizedDietId = normalizeTag(diet.id);
      const normalizedDietTitle = normalizeTag(diet.title);
      const normalizedMealType = normalizeTag(diet.mealType ?? "");

      const tasks = allActivities
        .filter((activity) => {
          const rawTags = (activity.tags ?? []).map((tag) => String(tag ?? "").trim());
          const tagKeys = new Set(rawTags.map((tag) => normalizeTag(tag)));
          const lowerRawTags = rawTags.map((tag) => tag.toLocaleLowerCase("tr-TR"));

          const hasDietScope =
            tagKeys.has("diyet") ||
            tagKeys.has("diet") ||
            tagKeys.has("beslenme") ||
            normalizeTag(activity.iconKey ?? "") === "nutrition";

          const explicitDietLink = lowerRawTags.some((tag) => {
            const isLegacyLink = tag.startsWith("diyet:") || tag.startsWith("diet:");
            const isTargetLink = tag.startsWith("gorev_hedef:");
            if (!isLegacyLink && !isTargetLink) return false;
            const linked = normalizeTag(tag.split(":").slice(1).join(":"));
            return linked === normalizedDietId || linked === normalizedDietTitle || (normalizedMealType.length > 2 && linked === normalizedMealType);
          });

          const explicitKind = lowerRawTags.some((tag) => tag === "diyet" || tag === "diet" || tag === "gorev_tur:diyet" || tag === "gorev_tur:diet");

          const implicitDietLink =
            tagKeys.has(normalizedDietId) ||
            (normalizedDietTitle.length > 3 && tagKeys.has(normalizedDietTitle)) ||
            (normalizedMealType.length > 2 && tagKeys.has(normalizedMealType));

          if (!explicitDietLink && !(explicitKind && implicitDietLink) && !(hasDietScope && implicitDietLink)) return false;

          const taskDay = parseDietTaskDay(activity.tags ?? []);
          return taskDay <= 0 || taskDay === programDay;
        })
        .map((activity) => ({
          id: activity.id,
          title: activity.title,
          description: activity.description,
          dayNumber: parseDietTaskDay(activity.tags ?? []),
          points: parseDietTaskPoints(activity),
        }))
        .sort((a, b) => a.dayNumber - b.dayNumber || b.points - a.points || a.title.localeCompare(b.title, "tr"));

      byDietId[diet.id] = tasks;
    });

    return byDietId;
  }, [activitiesQuery.data, programDayTracking.startedAtByItemId, shown, todayKey]);

  const isDietTaskCompleted = (dietId: string, taskId: string) => {
    const completed = dietTaskProgress.completedTaskIdsByDietIdByDate[todayKey]?.[dietId] ?? [];
    return completed.includes(taskId);
  };

  const toggleDietTask = (diet: DietPlan, task: DietProgramTask) => {
    let completedNow = false;

    setDietTaskProgress((prev) => {
      const byDate = prev.completedTaskIdsByDietIdByDate[todayKey] ?? {};
      const current = byDate[diet.id] ?? [];
      const exists = current.includes(task.id);
      completedNow = !exists;
      const nextItems = exists ? current.filter((id) => id !== task.id) : [...current, task.id];
      const next: DietTaskProgressState = {
        completedTaskIdsByDietIdByDate: {
          ...prev.completedTaskIdsByDietIdByDate,
          [todayKey]: {
            ...byDate,
            [diet.id]: nextItems,
          },
        },
      };
      saveDietTaskProgress(userKey, next);
      return next;
    });

    if (!completedNow) return;

    const taskMetadata = {
      kind: "diyet",
      task_category: "diet",
      points: task.points,
      taskTitle: task.title,
      dietId: diet.id,
      dietTitle: diet.title,
      programDay: resolveProgramDay(programDayTracking.startedAtByItemId[diet.id], todayKey),
    };

    void sendProgressEvent({
      eventType: "task_completed",
      dateKey: todayKey,
      sourceId: task.id,
      metadata: taskMetadata,
    }).catch(() => {
      // non-blocking progress logging
    });

    void sendProgressEvent({
      eventType: "diet_log_filled",
      dateKey: todayKey,
      sourceId: `diet-log:${diet.id}:${task.id}:${todayKey}`,
      metadata: taskMetadata,
    }).catch(() => {
      // non-blocking progress logging
    });

    if (isWaterGoalTask(task)) {
      void sendProgressEvent({
        eventType: "water_goal_completed",
        dateKey: todayKey,
        sourceId: `water-goal:${diet.id}:${task.id}:${todayKey}`,
        metadata: {
          ...taskMetadata,
          waterTargetLiters: diet.waterTargetLiters ?? null,
        },
      }).catch(() => {
        // non-blocking progress logging
      });
    }
  };

  const cancelDietProgram = (diet: DietPlan) => {
    setProgramDayTracking((prev) => {
      if (!prev.startedAtByItemId[diet.id]) return prev;
      const nextStartedAt = { ...prev.startedAtByItemId };
      delete nextStartedAt[diet.id];
      const next = { startedAtByItemId: nextStartedAt };
      saveProgramDayTracking(userKey, next);
      return next;
    });

    setDietTaskProgress((prev) => {
      const nextByDate: Record<string, Record<string, string[]>> = {};
      Object.entries(prev.completedTaskIdsByDietIdByDate).forEach(([dateKey, byDietId]) => {
        const nextByDietId = { ...byDietId };
        delete nextByDietId[diet.id];
        if (Object.keys(nextByDietId).length) {
          nextByDate[dateKey] = nextByDietId;
        }
      });

      const next = { completedTaskIdsByDietIdByDate: nextByDate };
      saveDietTaskProgress(userKey, next);
      return next;
    });

    toast({
      title: "Program iptal edildi",
      description: `${diet.title} programı durduruldu. İstersen tekrar başlatabilirsin.`,
    });
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8">
      <ProfileOverviewHeader />
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">Diyet Planı</div>
          <div className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-400">
            Etiketlere göre size uygun diyet önerileri.
          </div>
        </div>

        <div className="w-full max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Diyet ara..."
              className="h-11 rounded-2xl bg-white pl-10 shadow-sm dark:bg-slate-900"
            />
          </div>
        </div>
      </div>

      <Card className="border-slate-200 bg-white p-6 dark:border-[var(--panel-primary)]/10 dark:bg-slate-900/40">
        <div className="flex items-center gap-2 text-base font-bold text-slate-900 dark:text-white">
          <Sparkles className="h-4 w-4 text-[var(--panel-primary)]" />
          Kişisel Etiketler
        </div>
        <div className="mt-3 text-sm text-slate-600 dark:text-slate-400">
          {karmicCodes.length ? <div>Karmik Borç: {karmicCodes.join(", ")}</div> : <div>Karmik borç yok (genel öneriler).</div>}
        </div>
      </Card>

      <section>
        {dietsQuery.isLoading ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">Diyetler yükleniyor...</div>
        ) : dietsQuery.isError ? (
          <div className="text-sm text-red-600">{dietsQuery.error instanceof Error ? dietsQuery.error.message : "Yüklenemedi."}</div>
        ) : shown.length ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {shown.map((d) => {
              const programDays = parseProgramDaysFromTags(d.tags ?? []);
              const programDay = resolveProgramDay(programDayTracking.startedAtByItemId[d.id], todayKey);
              const dietTasks = dailyTasksByDietId[d.id] ?? [];
              const programStarted = Boolean(programDayTracking.startedAtByItemId[d.id]);

              return (
                <Card
                  key={d.id}
                  className="overflow-hidden border-slate-200 bg-white shadow-sm dark:border-[var(--panel-primary)]/10 dark:bg-slate-900/40"
                >
                {d.coverImageUrl ? (
                  <div className="aspect-[16/9] bg-slate-950">
                    <img src={d.coverImageUrl} alt="" className="h-full w-full object-cover opacity-90" />
                  </div>
                ) : null}

                <div className="p-6">
                  <div className="text-lg font-extrabold text-slate-900 dark:text-white">{d.title}</div>
                  <div className="mt-2 text-sm text-slate-600 dark:text-slate-400">{d.description}</div>

                  <div className="mt-4 flex flex-wrap gap-2 text-xs">
                    {d.waterTargetLiters !== undefined ? (
                      <span className="rounded-full bg-[var(--panel-primary)]/10 px-3 py-1 font-semibold text-[var(--panel-primary)]">
                        Su: {d.waterTargetLiters} L
                      </span>
                    ) : null}
                    {d.mealType ? (
                      <span className="rounded-full bg-slate-900/5 px-3 py-1 font-semibold text-slate-700 dark:bg-white/10 dark:text-slate-200">
                        {d.mealType}
                      </span>
                    ) : null}
                    {programDays > 0 ? (
                      <span className="rounded-full bg-[var(--panel-primary)]/10 px-3 py-1 font-black text-[var(--panel-primary)]">
                        {programDayTracking.startedAtByItemId[d.id] ? `${programDay}/${programDays}. gün` : `${programDays} gün program`}
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-4 flex flex-wrap gap-1">
                    {stripProgramDaysTag(d.tags ?? []).slice(0, 6).map((t, idx) => (
                      <span
                        key={`${d.id}-tag-${idx}`}
                        className="rounded-full bg-slate-900/5 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-700 dark:bg-white/10 dark:text-slate-200"
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  {programStarted ? (
                    <div className="mt-5 rounded-2xl border border-slate-200/80 bg-slate-50/80 p-3 dark:border-slate-700/60 dark:bg-slate-950/30">
                      <div className="mb-2 text-xs font-black uppercase tracking-[0.14em] text-slate-500 dark:text-slate-300">
                        {programDays > 0 ? `${programDay}. gün görevleri` : "Günlük görevler"}
                      </div>

                      {activitiesQuery.isLoading ? (
                        <div className="text-xs text-slate-500 dark:text-slate-400">Görevler hazırlanıyor...</div>
                      ) : activitiesQuery.isError ? (
                        <div className="text-xs text-red-600">Aktivite görevleri yüklenemedi.</div>
                      ) : dietTasks.length ? (
                        <div className="space-y-2">
                          {dietTasks.map((task) => {
                            const completed = isDietTaskCompleted(d.id, task.id);
                            return (
                              <button
                                key={`${d.id}-${task.id}`}
                                type="button"
                                onClick={() => toggleDietTask(d, task)}
                                className="flex w-full items-start gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-left transition hover:border-[var(--panel-primary)]/40 dark:border-slate-700 dark:bg-slate-900/40"
                              >
                                <span className="mt-0.5 text-[var(--panel-primary)]">
                                  {completed ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className={`block text-sm font-semibold ${completed ? "text-slate-400 line-through" : "text-slate-800 dark:text-slate-100"}`}>
                                    {task.title}
                                  </span>
                                  {task.description ? (
                                    <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">{task.description}</span>
                                  ) : null}
                                </span>
                                <span className="shrink-0 rounded-full bg-[var(--panel-primary)]/10 px-2 py-1 text-[10px] font-black text-[var(--panel-primary)]">
                                  +{task.points} puan
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-xs text-slate-500 dark:text-slate-400">Bugüne atanmış görev bulunmuyor.</div>
                      )}
                    </div>
                  ) : programDays > 0 ? (
                    <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 p-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900/30 dark:text-slate-300">
                      Programı başlatınca günlük görevler burada görünecek.
                    </div>
                  ) : null}

                  {programDays > 0 ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (programStarted) return;

                          let startedNow = false;
                          setProgramDayTracking((prev) => {
                            if (prev.startedAtByItemId[d.id]) return prev;
                            startedNow = true;
                            const next = {
                              startedAtByItemId: {
                                ...prev.startedAtByItemId,
                                [d.id]: todayKey,
                              },
                            };
                            saveProgramDayTracking(userKey, next);
                            return next;
                          });

                          if (!startedNow || !user?.id) return;

                          void createSocialPost({ body: `Diyet programıma başladım: ${d.title}` })
                            .then(async () => {
                              toast({
                                title: "Diyet programı başlatıldı",
                                description: "Başlangıç akışta otomatik paylaşıldı.",
                              });

                              await Promise.all([
                                queryClient.invalidateQueries({ queryKey: ["social-feed-posts", user.id] }),
                                queryClient.invalidateQueries({ queryKey: ["social-feed-activities", user.id] }),
                                queryClient.invalidateQueries({ queryKey: ["social-hub-posts", user.id] }),
                              ]);
                            })
                            .catch(() => {
                              // automatic sharing failure should not block diet flow
                            });

                        }}
                        className="rounded-xl border border-[var(--panel-primary)]/30 bg-[var(--panel-primary)]/10 px-3 py-2 text-xs font-black text-[var(--panel-primary)]"
                      >
                        {programStarted ? "Program devam ediyor" : "Programı başlat"}
                      </button>

                      {programStarted ? (
                        <button
                          type="button"
                          onClick={() => {
                            const confirmed = window.confirm("Programı iptal etmek istediğinize emin misiniz?");
                            if (!confirmed) return;
                            cancelDietProgram(d);
                          }}
                          className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-black text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300"
                        >
                          Programı iptal et
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-slate-600 dark:text-slate-400">Size uygun bir diyet önerisi bulunamadı.</div>
        )}
      </section>
    </div>
  );
}
